import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/context/CookieConsentContext";

const easing = [0.22, 0.68, 0.35, 1] as const;

const CookieConsent = () => {
  const {
    preferences,
    hasDecided,
    isSettingsOpen,
    openSettings,
    closeSettings,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent();

  const [analytics, setAnalytics] = useState(preferences.analytics);
  const [marketing, setMarketing] = useState(preferences.marketing);

  useEffect(() => {
    setAnalytics(preferences.analytics);
    setMarketing(preferences.marketing);
  }, [preferences, isSettingsOpen]);

  const showBanner = !hasDecided && !isSettingsOpen;

  // Publish visibility as a :root CSS variable so MobileStickyCta (and any
  // other overlay) can suppress itself while the banner is up — preventing
  // the two from stacking on mobile.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty(
      "--cookie-consent-visible",
      showBanner ? "1" : "0"
    );
    return () => {
      document.documentElement.style.setProperty("--cookie-consent-visible", "0");
    };
  }, [showBanner]);

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.6, ease: easing }}
            className="fixed inset-x-3 bottom-3 z-[60] sm:inset-x-6 sm:bottom-6"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            role="dialog"
            aria-live="polite"
            aria-label="Cookie-voorkeuren"
          >
            <div className="mx-auto max-w-3xl rounded-2xl border border-border/60 bg-background/95 p-5 shadow-[0_20px_60px_-20px_hsl(25_20%_18%_/_0.25)] backdrop-blur-xl sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
                <div className="flex-1 space-y-2">
                  <h2 className="font-serif text-lg text-foreground sm:text-xl">
                    Met zorg voor jouw privacy
                  </h2>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    We gebruiken alleen noodzakelijke cookies om de site te laten werken. Met jouw
                    toestemming meten we anoniem hoe bezoekers de site beleven, zodat we hem rustig kunnen
                    verbeteren. Lees meer in ons{" "}
                    <Link to="/privacybeleid" className="underline underline-offset-4 hover:text-foreground">
                      privacybeleid
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:min-w-[180px]">
                  <Button onClick={acceptAll} className="w-full">
                    Alles accepteren
                  </Button>
                  <Button onClick={rejectAll} variant="outline" className="w-full">
                    Alleen noodzakelijke
                  </Button>
                  <button
                    type="button"
                    onClick={openSettings}
                    className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Voorkeuren aanpassen
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={isSettingsOpen} onOpenChange={(open) => (open ? openSettings() : closeSettings())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cookie-voorkeuren</DialogTitle>
            <DialogDescription>
              Kies welke cookies we mogen gebruiken. Je kan deze keuze altijd later aanpassen via de link
              onderaan de pagina.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 bg-secondary/30 p-4">
              <div className="space-y-1">
                <p className="font-medium text-foreground">Noodzakelijk</p>
                <p className="text-sm text-muted-foreground">
                  Vereist voor formulieren, beveiliging en basisfunctionaliteit. Deze staan altijd aan.
                </p>
              </div>
              <Switch checked disabled aria-label="Noodzakelijke cookies (altijd aan)" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-4">
              <div className="space-y-1">
                <p className="font-medium text-foreground">Analytisch</p>
                <p className="text-sm text-muted-foreground">
                  Anoniem inzicht in welke pagina's bezoekers waarderen, zodat we de site rustig kunnen
                  verbeteren.
                </p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label="Analytische cookies"
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-4">
              <div className="space-y-1">
                <p className="font-medium text-foreground">Marketing</p>
                <p className="text-sm text-muted-foreground">
                  Helpt ons te begrijpen welke campagnes ouders bereiken die op zoek zijn naar onze
                  begeleiding.
                </p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label="Marketing cookies"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" onClick={rejectAll} className="sm:mr-auto">
              Alles weigeren
            </Button>
            <Button variant="ghost" onClick={() => savePreferences({ analytics, marketing })}>
              Selectie bewaren
            </Button>
            <Button onClick={acceptAll}>Alles accepteren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
